import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { ChartCard, EmptyState } from '@/components/ChartCard';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { formatPct, formatNumber } from '@/lib/format';
import { CHART_COLORS, PROVIDERS } from '@/constants/providers';
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, LineChart, Line, Legend } from 'recharts';

export default function AnalyticsTab({ project }) {
  const [byProvider, setByProvider] = useState([]);
  const [prompts, setPrompts] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [bp, pr, tl] = await Promise.all([
          api.get(`/projects/${project.id}/analytics/by-provider`),
          api.get(`/projects/${project.id}/analytics/prompts`),
          api.get(`/projects/${project.id}/analytics/timeline`),
        ]);
        setByProvider(bp.data.providers || []);
        setPrompts(pr.data.prompts || []);
        setTimeline((tl.data.points || []).map((p, i) => ({ ...p, x: p.date || `#${i + 1}` })));
      } finally {
        setLoading(false);
      }
    })();
  }, [project.id]);

  if (loading) {
    return <div className="space-y-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-64" />)}</div>;
  }

  const hasData = byProvider.length > 0 || prompts.some((p) => p.runs > 0);

  return (
    <div className="space-y-4">
      {!hasData ? (
        <ChartCard title="Analytics" subtitle="Deep dive into your data">
          <EmptyState title="No analytics yet" description="Run at least one scan to see analytics." />
        </ChartCard>
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <ChartCard title="Per-provider mentions" subtitle="Total answers vs answers mentioning your brand">
              <div className="h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={byProvider.map((p) => ({ name: PROVIDERS[p.provider]?.short || p.provider, mentioned: p.mentioned, total: p.runs }))} margin={{ top: 10, right: 10, bottom: 0, left: -10 }}>
                    <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" opacity={0.6} />
                    <XAxis dataKey="name" tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }} />
                    <YAxis tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }} />
                    <Tooltip contentStyle={{ borderRadius: 10, borderColor: 'hsl(var(--border))' }} />
                    <Legend iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="total" name="Total answers" fill={CHART_COLORS[4]} radius={[8, 8, 0, 0]} />
                    <Bar dataKey="mentioned" name="Mentioned brand" fill={CHART_COLORS[0]} radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </ChartCard>

            <ChartCard title="Sentiment & mention trend" subtitle="Across scans">
              <div className="h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={timeline} margin={{ top: 10, right: 10, bottom: 0, left: -10 }}>
                    <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" opacity={0.6} />
                    <XAxis dataKey="x" tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }} />
                    <YAxis yAxisId="left" tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }} domain={[0, 1]} tickFormatter={(v) => `${Math.round(v * 100)}%`} />
                    <YAxis yAxisId="right" orientation="right" tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }} domain={[-1, 1]} />
                    <Tooltip contentStyle={{ borderRadius: 10, borderColor: 'hsl(var(--border))' }} />
                    <Legend iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                    <Line yAxisId="left" type="monotone" dataKey="mention_rate" name="Mention rate" stroke={CHART_COLORS[0]} strokeWidth={2} />
                    <Line yAxisId="right" type="monotone" dataKey="avg_sentiment" name="Sentiment" stroke={CHART_COLORS[2]} strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </ChartCard>
          </div>

          <ChartCard title="Prompt performance" subtitle="Which prompts get your brand cited most">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground border-b">
                    <th className="py-2 pr-3">Prompt</th>
                    <th className="py-2 pr-3">Category</th>
                    <th className="py-2 pr-3 text-right">Runs</th>
                    <th className="py-2 pr-3 text-right">Mentioned</th>
                    <th className="py-2 pr-3 text-right">Mention rate</th>
                    <th className="py-2 pr-3 text-right">Sentiment</th>
                  </tr>
                </thead>
                <tbody>
                  {prompts.filter((p) => p.runs > 0).map((p) => (
                    <tr key={p.prompt_id} className="border-b last:border-b-0 hover:bg-secondary/40" data-testid={`prompt-perf-${p.prompt_id}`}>
                      <td className="py-3 pr-3 max-w-md">
                        <div className="truncate" title={p.text}>{p.text}</div>
                      </td>
                      <td className="py-3 pr-3"><Badge variant="secondary" className="text-[11px]">{p.category}</Badge></td>
                      <td className="py-3 pr-3 text-right tabular">{formatNumber(p.runs)}</td>
                      <td className="py-3 pr-3 text-right tabular">{formatNumber(p.mentioned)}</td>
                      <td className="py-3 pr-3 text-right tabular font-semibold">{formatPct(p.mention_rate)}</td>
                      <td className="py-3 pr-3 text-right tabular">{p.avg_sentiment.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {prompts.filter((p) => p.runs > 0).length === 0 && (
                <div className="py-6 text-center text-sm text-muted-foreground">No prompt data yet.</div>
              )}
            </div>
          </ChartCard>
        </>
      )}
    </div>
  );
}
